<template>
  <button v-show="visible"
          class="back-to-top"
          :style="posStyle"
          @click="scrollTop"
          aria-label="Back to top">
    <span class="icon">△</span>
  </button>
</template>

<script>
export default {
  name: 'BackToTop',
  props: {
    threshold: { type: Number, default: 240 },
    right: { type: Number, default: 24 },
    bottom: { type: Number, default: 24 },
  },
  data() {
    return { 
      visible: false,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
    }
  },
  computed: {
    posStyle() {
      const width = this.windowWidth
      // ✅ 根据屏幕尺寸调整位置
      let right = this.right
      let bottom = this.bottom
      if (width <= 480) {
        right = 12
        bottom = 80  // 避免被底部导航遮挡
      } else if (width <= 768) {
        right = 16
        bottom = 70
      }
      return { right: right + 'px', bottom: bottom + 'px' }
    },
    // ✅ 动态计算按钮大小
    buttonSize() {
      const width = this.windowWidth
      if (width <= 480) return 40
      if (width <= 768) return 42
      return 44
    },
    // ✅ 动态计算图标大小
    iconSize() {
      const width = this.windowWidth
      if (width <= 480) return 16
      if (width <= 768) return 17
      return 18
    },
  },
  methods: {
    onScroll() {
      const y = window.scrollY || document.documentElement.scrollTop || 0
      // ✅ 手机端降低触发阈值，更早显示
      const threshold = this.windowWidth <= 768 ? 150 : this.threshold
      this.visible = y > threshold
    },
    scrollTop() {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (e) {
        window.scrollTo(0, 0)
      }
    },
    handleResize() {
      this.windowWidth = window.innerWidth
      this.onScroll()
    },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.handleResize)
    this.onScroll()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.handleResize)
  },
}
</script>

<style scoped>
/* ============================================
   基础样式
   ============================================ */
.back-to-top {
  position: fixed;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  outline: none;
  cursor: pointer;
  background: #4285f4;
  color: #fff;
  box-shadow: 0 4px 16px rgba(66, 133, 244, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  transform: scale(0.8) translateY(20px);
  pointer-events: none;
  -webkit-tap-highlight-color: transparent;
}

/* ✅ 显示状态 */
.back-to-top:not([style*="display: none"]) {
  opacity: 1;
  transform: scale(1) translateY(0);
  pointer-events: auto;
}

.back-to-top:hover {
  background: #357ae8;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 24px rgba(66, 133, 244, 0.4);
}

.back-to-top:active {
  transform: scale(0.92);
  transition-duration: 0.1s;
}

.icon {
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-2px);
}

/* ============================================
   动画
   ============================================ */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes fadeOutDown {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.8) translateY(20px);
  }
}

/* ============================================
   手机端适配 (<= 768px)
   ============================================ */
@media (max-width: 768px) {
  .back-to-top {
    width: 42px;
    height: 42px;
    box-shadow: 0 4px 16px rgba(66, 133, 244, 0.3);
  }

  .icon {
    font-size: 17px;
  }

  .back-to-top:active {
    transform: scale(0.9);
  }

  .back-to-top:hover {
    transform: scale(1.02);
  }
}

/* ============================================
   小屏手机适配 (<= 480px)
   ============================================ */
@media (max-width: 480px) {
  .back-to-top {
    width: 40px;
    height: 40px;
    box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
  }

  .icon {
    font-size: 16px;
  }

  .back-to-top:active {
    transform: scale(0.88);
  }
}

/* ============================================
   暗黑模式适配
   ============================================ */
@media (prefers-color-scheme: dark) {
  .back-to-top {
    background: #5a9aff;
    box-shadow: 0 4px 16px rgba(90, 154, 255, 0.3);
  }

  .back-to-top:hover {
    background: #6ba8ff;
  }
}
</style>